import * as bootstrap from 'bootstrap';
import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { AuthController } from '../controllers/auth-controller';
import { NotificationsController } from '../controllers/notifications-controller';
import { store } from '../redux/store';
import { formatPhoneNumber } from '../../utils/formatPhoneNumber';

@customElement('tm-register-modal')
export class RegisterModal extends LitElement {
    createRenderRoot() {
        return this;
    }

    authController = new AuthController(this);
    notificationController = new NotificationsController(this, this.authController);
    dispatch = store.dispatch;
    get store() {
        return this.authController.state;
    }

    @state() isLoading: boolean = false;
    @state() errorMessage: string = '';
    @state() whatsappErrorMessage: boolean = false;

    @state()
    private profileType: string = '';

    profileTypeOptions: {
        label: string;
        value: string;
    }[] = [
        {
            label: 'Aluno',
            value: 'student',
        },
        {
            label: 'Pai/Mãe',
            value: 'parent',
        },
        {
            label: 'Professor',
            value: 'professor',
        },
        {
            label: 'Instituição',
            value: 'institution',
        },
    ];
    schoolYearOptions: {
        label: string;
        value: string;
    }[] = [
        {
            label: 'Educação Infantil',
            value: 'educacao-infantil',
        },
        {
            label: '1° Ano - Ensino Fundamental',
            value: '1-ano-ensino-fundamental',
        },
        {
            label: '2° Ano - Ensino Fundamental',
            value: '2-ano-ensino-fundamental',
        },
        {
            label: '3° Ano - Ensino Fundamental',
            value: '3-ano-ensino-fundamental',
        },
        {
            label: '4° Ano - Ensino Fundamental',
            value: '4-ano-ensino-fundamental',
        },
        {
            label: '5° Ano - Ensino Fundamental',
            value: '5-ano-ensino-fundamental',
        },
        {
            label: '6° Ano - Ensino Fundamental',
            value: '6-ano-ensino-fundamental',
        },
        {
            label: '7° Ano - Ensino Fundamental',
            value: '7-ano-ensino-fundamental',
        },
        {
            label: '8° Ano - Ensino Fundamental',
            value: '8-ano-ensino-fundamental',
        },
        {
            label: '9° Ano - Ensino Fundamental',
            value: '9-ano-ensino-fundamental',
        },
        {
            label: '1° Ano - Ensino Médio',
            value: '1-ano-ensino-medio',
        },
        {
            label: '2° Ano - Ensino Médio',
            value: '2-ano-ensino-medio',
        },
        {
            label: '3° Ano - Ensino Médio',
            value: '3-ano-ensino-medio',
        },
        {
            label: 'Ensino Superior',
            value: 'ensino-superior',
        },
        {
            label: 'Pós-Graduação',
            value: 'pos-graduacao',
        },
        {
            label: 'Mestrado',
            value: 'mestrado',
        },
        {
            label: 'Doutorado',
            value: 'doutorado',
        },
    ];

    entranceExamOptions: {
        label: string;
        value: string;
    }[] = [
        {
            label: 'ENEM',
            value: 'enem',
        },
        {
            label: 'FUVEST',
            value: 'fuvest',
        },
        {
            label: 'UNICAMP',
            value: 'unicamp',
        },
        {
            label: 'UNESP',
            value: 'unesp',
        },
        {
            label: 'UFPA',
            value: 'ufpa',
        },
        {
            label: 'UFRJ',
            value: 'ufrj',
        },
        {
            label: 'Outra prova',
            value: 'other',
        },
        {
            label: 'Não',
            value: 'none',
        },
    ];

    emitClose() {
        (window as any).$crisp.push(['config', 'container:index', [1000000]]);
        const jotformStyle = document.querySelector('#tm-register-modal-style');
        if (jotformStyle) {
            jotformStyle.remove();
        }
        this.dispatchEvent(new CustomEvent('close'));
        this.notificationController.getPendingNotifications();
    }

    dismissModal() {
        const modalElement = document.querySelector('#tm-register-modal');
        if (modalElement) {
            const bootstrapModal = bootstrap.Modal.getInstance(modalElement);
            bootstrapModal.hide();
        }
    }

    showModal() {
        const modalElement = document.querySelector('#tm-register-modal');
        if (modalElement) {
            const bootstrapModal = bootstrap.Modal.getOrCreateInstance(modalElement);
            bootstrapModal.show();

            modalElement.addEventListener(
                'hidden.bs.modal',
                () => {
                    this.emitClose();
                },
                {
                    once: true,
                }
            );
        }
        (window as any).$crisp.push(['config', 'container:index', [1]]);
        const style = document.createElement('style');
        style.id = 'tm-register-modal-style';
        style.innerHTML = `
            .jfFeedbackButton {
                z-index: 1;
            }
        `;
        document.body.appendChild(style);
    }
    handleNumberInput(event: Event) {
        this.errorMessage = '';
        this.whatsappErrorMessage = false;
        const input = event.target as HTMLInputElement;
        let value = input.value;
        this.requestUpdate();
        input.value = formatPhoneNumber(value);
        this.requestUpdate();
    }

    handleProfileTypeChange(event: Event) {
        this.errorMessage = '';
        this.whatsappErrorMessage = false;
        const input = event.target as HTMLInputElement;
        this.profileType = input.value;
    }

    firstUpdated() {
        (window as any).$crisp = (window as any).$crisp || [];
        this.showModal();
    }

    get whatsappIsValid() {
        const whatsapp = document.querySelector('#whatsapp') as HTMLInputElement;
        if (!whatsapp) return false;
        return whatsapp.value.length >= 14;
    }

    parseFormData(formElement: HTMLFormElement) {
        if (!formElement) return null;
        const formData = new FormData(formElement);
        const data = Object.fromEntries(formData.entries());

        return {
            profile_type: data['profile-type']?.toString() ?? undefined,
            school_year: data['school-year']?.toString() ?? undefined,
            phone_number: data['whatsapp']?.toString() ?? undefined,
            entrance_exam: data['entrance-exam']?.toString() ?? undefined,
        };
    }

    async handleSubmit(event: Event) {
        event.preventDefault();
        if (this.isLoading) return;
        if (!this.whatsappIsValid) {
            this.whatsappErrorMessage = true;
            return;
        }
        const formData = this.parseFormData(event.target as HTMLFormElement);
        if (!formData) return;
        this.isLoading = true;
        this.errorMessage = '';
        this.isLoading = true;
        const response = await this.authController.updateUser(formData);
        if ('error' in response) {
            this.errorMessage = (response.error as any)?.data?.data || 'Erro ao atualizar perfil';
            console.error(response);
        } else {
            this.dismissModal();
        }
        this.isLoading = false;
    }

    renderBrFlag() {
        return html`
            <svg
                width="20"
                height="21"
                viewBox="0 0 20 21"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <g clip-path="url(#clip0_11766_44857)">
                    <path
                        d="M20 15.4996C20 16.0889 19.7659 16.6542 19.3491 17.0709C18.9324 17.4877 18.3671 17.7218 17.7778 17.7218H2.22222C1.63285 17.7218 1.06762 17.4877 0.650874 17.0709C0.234126 16.6542 0 16.0889 0 15.4996V5.49957C0 4.9102 0.234126 4.34496 0.650874 3.92822C1.06762 3.51147 1.63285 3.27734 2.22222 3.27734H17.7778C18.3671 3.27734 18.9324 3.51147 19.3491 3.92822C19.7659 4.34496 20 4.9102 20 5.49957V15.4996Z"
                        fill="#009B3A"
                    />
                    <path
                        d="M18.1823 10.4999L10.0001 16.6799L1.81787 10.4999L10.0001 4.31934L18.1823 10.4999Z"
                        fill="#FEDF01"
                    />
                    <path
                        d="M9.9867 14.0457C11.9682 14.0457 13.5745 12.4394 13.5745 10.4579C13.5745 8.47642 11.9682 6.87012 9.9867 6.87012C8.00523 6.87012 6.39893 8.47642 6.39893 10.4579C6.39893 12.4394 8.00523 14.0457 9.9867 14.0457Z"
                        fill="#002776"
                    />
                    <path
                        d="M6.8206 8.77057C6.63426 9.12172 6.50807 9.5016 6.44727 9.89445C8.66671 9.73334 11.6789 10.945 12.9717 12.4472C13.195 12.1117 13.3606 11.7361 13.4623 11.3339C11.8667 9.7739 9.06393 8.76168 6.8206 8.77057Z"
                        fill="#CBE9D4"
                    />
                    <path
                        d="M6.6665 10.6299H7.22206V11.1854H6.6665V10.6299ZM7.22206 11.741H7.77762V12.2965H7.22206V11.741Z"
                        fill="#88C9F9"
                    />
                    <path
                        d="M8.3335 10.6297H8.88905V11.1852H8.3335V10.6297ZM9.44461 11.1852H10.0002V11.7408H9.44461V11.1852ZM11.6668 12.2963H12.2224V12.8519H11.6668V12.2963ZM10.0002 12.8519H10.5557V13.4074H10.0002V12.8519ZM11.6668 9.51855H12.2224V10.0741H11.6668V9.51855Z"
                        fill="#55ACEE"
                    />
                    <path d="M10.5557 11.7402H11.1112V12.2958H10.5557V11.7402Z" fill="#3B88C3" />
                </g>
                <defs>
                    <clipPath id="clip0_11766_44857">
                        <rect width="20" height="20" fill="white" transform="translate(0 0.5)" />
                    </clipPath>
                </defs>
            </svg>
        `;
    }

    render() {
        return html`
            <div
                class="modal fade tm-register-modal"
                id="tm-register-modal"
                tabindex="-1"
                aria-hidden="true"
            >
                <div class="modal-dialog">
                    <div class="modal-content">
                        <div class="tm-register-modal-bg">
                            <img
                                src="/img/special/login/noise-bg.png"
                                alt="Register Modal Background"
                                class="tm-register-modal-bg-img"
                            />
                            <img
                                src="/img/special/login/register-girl.png"
                                alt="Register Modal Background"
                                class="tm-register-modal-bg-girl"
                            />
                        </div>
                        <div class="tm-register-modal-content">
                            <button class="tm-register-modal-close" @click=${this.dismissModal}>
                                <span class="icon icon-012-close"> </span>
                            </button>
                            <div class="tm-register-modal-title">Quase pronto!</div>
                            <div class="tm-register-modal-subtitle">
                                Personalize sua experiência 🚀
                            </div>
                            <form
                                class="tm-register-modal-form"
                                @submit=${this.handleSubmit}
                                id="tm-register-modal-form"
                            >
                                <div class="tm-register-modal-form-group">
                                    <label
                                        class="tm-register-modal-form-group-label"
                                        for="whatsapp"
                                    >
                                        Adicione seu WhatsApp e fique por dentro!
                                    </label>
                                    <div
                                        class="${classMap({
                                            'tm-register-modal-form-group-input': true,
                                            'tm-register-modal-form-group-input--error':
                                                this.whatsappErrorMessage,
                                        })}"
                                        type="text"
                                    >
                                        <div class="tm-register-modal-form-group-input-wh-prefix">
                                            ${this.renderBrFlag()} +55
                                        </div>
                                        <input
                                            id="whatsapp"
                                            name="whatsapp"
                                            type="tel"
                                            placeholder="(00) 00000-0000"
                                            @input=${this.handleNumberInput}
                                        />
                                    </div>
                                    ${this.whatsappErrorMessage
                                        ? html`<div class="tm-register-modal-form-group-error">
                                              Campo WhatsApp é obrigatório
                                          </div>`
                                        : nothing}
                                </div>
                                <div class="tm-register-modal-form-group">
                                    <label
                                        class="tm-register-modal-form-group-label"
                                        for="profile-type"
                                    >
                                        Inscreva-se como:
                                    </label>
                                    <select
                                        class="tm-register-modal-form-group-input tm-register-modal-form-group-input--select"
                                        name="profile-type"
                                        id="profile-type"
                                        @change=${this.handleProfileTypeChange}
                                    >
                                        <option value="" selected disabled>
                                            Selecione uma opção
                                        </option>
                                        ${this.profileTypeOptions.map(
                                            (option) =>
                                                html`<option value="${option.value}">
                                                    ${option.label}
                                                </option>`
                                        )}
                                    </select>
                                </div>
                                ${this.profileType === 'student' || this.profileType === ''
                                    ? html` <div class="tm-register-modal-form-group">
                                              <label
                                                  class="${classMap({
                                                      'tm-register-modal-form-group-label': true,
                                                      'tm-register-modal-form-group-label--disabled':
                                                          this.profileType !== 'student',
                                                  })}"
                                                  for="school-year"
                                              >
                                                  Qual o seu grau escolar?
                                              </label>
                                              <select
                                                  class="${classMap({
                                                      'tm-register-modal-form-group-input': true,
                                                      'tm-register-modal-form-group-input--select':
                                                          true,
                                                      'tm-register-modal-form-group-input--disabled':
                                                          this.profileType !== 'student',
                                                  })}"
                                                  .disabled=${this.profileType !== 'student'}
                                                  name="school-year"
                                                  id="school-year"
                                              >
                                                  <option value="" selected disabled>
                                                      Selecione uma opção
                                                  </option>
                                                  ${this.schoolYearOptions.map(
                                                      (option) =>
                                                          html`<option value="${option.value}">
                                                              ${option.label}
                                                          </option>`
                                                  )}
                                              </select>
                                          </div>
                                          <div class="tm-register-modal-form-group">
                                              <label
                                                  class="${classMap({
                                                      'tm-register-modal-form-group-label': true,
                                                      'tm-register-modal-form-group-label--select':
                                                          true,
                                                      'tm-register-modal-form-group-label--disabled':
                                                          this.profileType !== 'student',
                                                  })}"
                                                  for="entrance-exam"
                                              >
                                                  Estudando para alguma prova?
                                              </label>
                                              <select
                                                  class="${classMap({
                                                      'tm-register-modal-form-group-input': true,
                                                      'tm-register-modal-form-group-input--select':
                                                          true,
                                                      'tm-register-modal-form-group-input--disabled':
                                                          this.profileType !== 'student',
                                                  })}"
                                                  .disabled=${this.profileType !== 'student'}
                                                  name="entrance-exam"
                                                  id="entrance-exam"
                                              >
                                                  <option value="" selected disabled>
                                                      Selecione uma opção
                                                  </option>
                                                  ${this.entranceExamOptions.map(
                                                      (option) =>
                                                          html`<option value="${option.value}">
                                                              ${option.label}
                                                          </option>`
                                                  )}
                                              </select>
                                          </div>`
                                    : nothing}
                                ${this.errorMessage
                                    ? html`<div class="form-message form-message--error">
                                          ${this.errorMessage}
                                      </div>`
                                    : nothing}
                                <div class="tm-register-modal-form-submit-container">
                                    <button
                                        type="submit"
                                        .disabled=${this.isLoading}
                                        class="tm-register-modal-form-button ds-button ds-button--primary"
                                    >
                                        Finalizar
                                        <span class="icon icon-034-dash-arrow"> </span>
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        `;
    }
}
